import { useEffect, useMemo } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useProfileTileSave } from '@/components/profile';
import { ROLES, ROUTES } from '@/constants';
import useAppDataContext from '@/contexts/app-data/useAppDataContext';
import { API } from '@/services/api.service';
import {
  CALCULATION_METHOD_BY_TYPE_OPTIONS,
  CALCULATION_METHOD_OPTIONS,
  OWNERS_TYPE_OPTIONS,
  SALARY_CALCULATION_OPTIONS,
} from '@/shared/company';
import { CURRENCY_OPTIONS, QUERY_KEY } from '@/shared/constants';
import { formatDateForBackend } from '@/utils/formUtils';
import { getUserFullName, translateOptions } from '@/utils/utils';
import buildCompanyProfileBody from '../companyProfileBody';
import { companySectionOfField } from '../companyProfileSections';

/**
 * Options, enum and user queries, the save mutation and the form handlers.
 * On the profile page also the open tile's save and discard, handed to the
 * page through `actionsRef` (for the leave-tile question).
 */
export default function useCompanyFormActions({
  actionsRef,
  baselineRef,
  companyAdminsReceiveHREmailsState,
  companyId,
  documentReturnTypeState,
  documentSeparateRequestResponseState,
  extendedAbExportChecked,
  form,
  formData,
  isEdited,
  isForEmployeesState,
  isProfile,
  isSickLeaveRequestState,
  navigate,
  notification,
  onFinishFailed,
  onSaved,
  ownershipStructureValues,
  profileSection,
  queryClient,
  setWorkTypesForSelect,
  t,
  typeOfHrState,
  weekendLeavesState,
  workTypesForSelect,
  workTypesKeys,
}) {
  // * OPTIONS FIELD
  const salaryCalculationOptions = useMemo(() => {
    return translateOptions(SALARY_CALCULATION_OPTIONS, t);
  }, [t]);

  // * CURRENCY OPTIONS
  const currencyOptions = useMemo(() => {
    return translateOptions(CURRENCY_OPTIONS, t);
  }, [t]);

  const getTranslatedCalculationOptionsForType = (type, t) => {
    const allOptions = CALCULATION_METHOD_OPTIONS;
    const allowedValues = CALCULATION_METHOD_BY_TYPE_OPTIONS[type] || [];
    const filteredOptions = allOptions.filter((opt) =>
      allowedValues.includes(opt.value)
    );

    return translateOptions(filteredOptions, t);
  };

  const filteredOwnershipStructure = useMemo(() => {
    return (
      ownershipStructureValues
        ?.filter(({ type }) => type === OWNERS_TYPE_OPTIONS.AUTHORIZED)
        .map(({ name }) => ({ name })) || []
    );
  }, [ownershipStructureValues]);

  const {
    enums: enumsData,
    isLoading: enumsDataIsLoading,
    isRefetching,
    onRefetch,
  } = useAppDataContext();

  const workTypeEnum = useMemo(() => {
    const base = enumsData?.companies?.workTypes || [];
    const extended = extendedAbExportChecked
      ? enumsData?.companies?.extendedWorkTypes || []
      : [];
    const source = [...base, ...extended];

    return source.length
      ? source.map((item) => {
          return {
            ...item,
            value: item.value,
            createsDocument: item.createsDocument,
            hrDropdown: item.hrDropdown,
          };
        })
      : [];
  }, [
    enumsData?.companies?.workTypes,
    enumsData?.companies?.extendedWorkTypes,
    extendedAbExportChecked,
  ]);

  useEffect(() => {
    if (workTypeEnum?.length) {
      const filteredWorkTypes = workTypeEnum.filter(
        ({ active, dropdown, parent }) =>
          active && dropdown && (parent === '' || !parent || false)
      );

      const selectedWorkTypes = formData?.calculationSettings?.workTypes || [];

      const defaultWorkTypes = filteredWorkTypes
        .filter((item) => item.default)
        .map((item) => item.key);

      form.setFieldsValue({
        calculationSettings: {
          workTypes: formData ? selectedWorkTypes : defaultWorkTypes,
        },
      });
      // Arrives after the loaded values; part of them, not a change.
      if (formData && baselineRef.current) {
        baselineRef.current = form.getFieldsValue(true);
      }

      setWorkTypesForSelect(filteredWorkTypes);
    }
  }, [workTypeEnum, form, workTypesKeys, isRefetching, formData]);

  const workTypesOptions = useMemo(() => {
    const sortedTypeOptions = workTypesForSelect?.toSorted((a, b) =>
      a.default === b.default ? 0 : a.default ? 1 : -1
    );

    const workTypes = sortedTypeOptions.map((option) => ({
      ...option,
      value: option.key,
      disabled: option.default,
    }));

    return workTypes?.filter(({ dropdown }) => dropdown);
  }, [workTypesForSelect]);

  // * clients, clientsOfficers, eInvoiceOfficers and admins dropdowns data
  const userSelect = { _id: 1, firstName: 1, lastName: 1, roles: 1 };

  const userParams = {
    roles: [
      ROLES.client,
      ROLES.clientOfficer,
      ROLES.eInvoiceOfficer,
      ROLES.admin,
    ],
    select: userSelect,
  };

  const getUsersByRole = (users, userRole) => {
    return users
      ?.filter((user) => user.roles.includes(userRole))
      .map((user) => ({
        value: user._id,
        label: getUserFullName(user?.firstName, user?.lastName),
      }));
  };

  const { data: usersData, isLoading: usersIsLoading } = useQuery({
    queryKey: [QUERY_KEY.usersDropdown, userParams],
    queryFn: async () => await API.users.retrieveDropdownAllUsers(userParams),
    select: (data) => {
      const users = data?.items || [];

      return {
        clients: getUsersByRole(users, ROLES.client),
        clientOfficers: getUsersByRole(users, ROLES.clientOfficer),
        eInvoiceOfficers: getUsersByRole(users, ROLES.eInvoiceOfficer),
        admins: getUsersByRole(users, ROLES.admin),
      };
    },
  });

  const clientsData = usersData?.clients || [];
  const clientOfficersData = usersData?.clientOfficers || [];
  const eInvoiceOfficersData = usersData?.eInvoiceOfficers || [];
  const adminsData = usersData?.admins || [];

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (payload) => {
      if (isEdited) {
        return await API.companies.updateCompanyById(companyId, payload);
      } else {
        return await API.companies.createNewCompany(payload);
      }
    },
    onSuccess: (response) => {
      form.resetFields();

      // The company query holds its answer for five minutes. Without dropping
      // it, reopening this form straight after a save re-rendered the values as
      // they were BEFORE the write — a setting that had just been changed came
      // back showing its old state, and a field the save removed came back
      // populated. The list is invalidated too, since it prints the same fields.
      //
      // 🚨 The id belongs in the key. `useCompany` caches under
      // [company, companyId], and React Query matches keys by PREFIX — so
      // invalidating [company] alone dropped every company anyone had opened,
      // not just the one that was saved, and each of them refetched on the next
      // visit.
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.company, companyId],
      });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.companies] });

      notification.success({
        message: t('success', { ns: 'messages' }),
        description: response.message,
      });

      // The profile page stays open.
      if (onSaved) {
        onSaved(response);
        return;
      }

      const navigateUrl = `${ROUTES.admin}/${ROUTES.companies}`;

      navigate(navigateUrl);
    },
    onError: (error) => {
      notification.error({
        message: t('error', { ns: 'messages' }),
        description: error.message,
      });
    },
  });

  const handleReset = () => {
    form.resetFields();
    onRefetch();
  };

  const onOwnerFinish = (values) => {
    form.setFieldsValue({ ...values });
  };

  // `current`: the whole form store, which the profile compares with the
  // loaded values; a tile save passes it limited to that tile.
  const onFinish = async (values, current = form.getFieldsValue(true)) => {
    const formValues = { ...values };

    if (formValues?.applicationSettings?.trackerStartDate) {
      formValues.applicationSettings.trackerStartDate = formatDateForBackend(
        formValues.applicationSettings.trackerStartDate
      );
    }

    // Profile page: only the changed groups (the server fills the rest of a
    // group in from what is stored); a save without changes sends nothing.
    if (isProfile && baselineRef.current) {
      const { body, hasChanges } = buildCompanyProfileBody({
        payload: formValues,
        current,
        base: baselineRef.current,
      });
      if (!hasChanges) {
        // Same toast as a save on the old page; nothing to send.
        notification.success({
          message: t('success', { ns: 'messages' }),
          description: t('employeeProfile.noChanges', { ns: 'translations' }),
        });
        onSaved?.();
        return;
      }
      await mutateAsync(body);
      return;
    }

    await mutateAsync(formValues);
  };

  const shouldHrGroup =
    typeOfHrState?.visible ||
    documentReturnTypeState?.visible ||
    documentSeparateRequestResponseState?.visible ||
    weekendLeavesState?.visible ||
    isForEmployeesState?.visible ||
    companyAdminsReceiveHREmailsState?.visible ||
    isSickLeaveRequestState?.visible;

  const saveTile = useProfileTileSave({
    form,
    baselineRef,
    sectionOfField: companySectionOfField,
    section: profileSection,
    onFinish,
    onFinishFailed,
  });

  // Back to the values as loaded, not to the raw defaults.
  const discardChanges = () => {
    handleReset();
    if (baselineRef.current) form.setFieldsValue(baselineRef.current);
  };

  useEffect(() => {
    if (actionsRef) {
      actionsRef.current = { save: saveTile, discard: discardChanges };
    }
  });

  return {
    discardChanges,
    saveTile,
    adminsData,
    clientOfficersData,
    clientsData,
    currencyOptions,
    eInvoiceOfficersData,
    enumsDataIsLoading,
    filteredOwnershipStructure,
    getTranslatedCalculationOptionsForType,
    handleReset,
    isPending,
    onFinish,
    onOwnerFinish,
    salaryCalculationOptions,
    shouldHrGroup,
    usersIsLoading,
    workTypesOptions,
  };
}
